import { useState, useRef, useEffect } from 'react';

type Props = {
  score: number | null;
  hasRating: boolean;
  editable: boolean;
  maxScore?: number;
  ratingScale?: Array<{ score: number; label: string }>;
  onSave: (score: number) => Promise<void>;
  saving?: boolean;
};

/**
 * Inline-editable score cell for the assignments table.
 * Click to edit, Enter/blur to save, Esc to cancel.
 */
export default function InlineRatingCell({ score, hasRating, editable, maxScore = 5, ratingScale = [], onSave, saving }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(score?.toString() ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (editing && selectRef.current && ratingScale.length > 0) {
      selectRef.current.focus();
    }
  }, [editing, ratingScale.length]);

  // Sync external changes
  useEffect(() => {
    if (!editing) setValue(score?.toString() ?? '');
  }, [score, editing]);

  const commit = async () => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > maxScore) {
      setEditing(false);
      setValue(score?.toString() ?? '');
      return;
    }
    try {
      await onSave(num);
      setEditing(false);
    } catch {
      // stay in editing mode on failure
    }
  };

  if (saving) {
    return <span className="inline-rating-saving">Saving…</span>;
  }

  const currentLabel = score !== null && score !== undefined
    ? (ratingScale.find((r) => r.score === Number(score))?.label || `${score}`)
    : '';

  if (!editable) {
    return (
      <span className={`pill ${hasRating ? 'pill-good' : 'pill-warn'}`}>
        {hasRating ? (currentLabel ? `${score} — ${currentLabel}` : score ?? '—') : '—'}
      </span>
    );
  }

  if (editing && ratingScale.length > 0) {
    return (
      <select
        ref={selectRef}
        className="inline-rating-select"
        value={value}
        onChange={async (e) => {
          const next = e.target.value;
          setValue(next);
          const num = Number(next);
          if (Number.isFinite(num) && num >= 0 && num <= maxScore) {
            await onSave(num);
            setEditing(false);
          }
        }}
        onBlur={() => setEditing(false)}
      >
        <option value="">Select rating</option>
        {ratingScale.map((item) => (
          <option key={item.score} value={item.score}>
            {item.label} ({item.score})
          </option>
        ))}
      </select>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={maxScore}
        step={0.5}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setValue(score?.toString() ?? ''); }
        }}
        onBlur={commit}
        className="inline-rating-input"
      />
    );
  }

  return (
    <button
      className={`inline-rating-btn ${hasRating ? 'has-score' : 'no-score'}`}
      onClick={() => setEditing(true)}
      title={editable ? 'Click to edit score' : ''}
    >
      {hasRating ? (currentLabel ? `${currentLabel} (${score})` : score ?? '—') : '+ Rate'}
    </button>
  );
}
